import React, { useContext } from "react";
import {
    Badge,
    BadgeColor,
    createListTableColumns,
    ensure,
    ListTableColumn,
    renderDate,
    renderDateTime,
    renderOptionalName,
    Stack,
    TableColumn
} from "sprint-common-web";

import { renderContent } from "../../common/components/listings/renderContent";
import { renderOrderResolutions } from "../../common/components/listings/renderOrderResolutions";
import { renderRuntime } from "../../common/components/listings/renderRuntime";
import { renderTargetFilenames } from "../../common/components/listings/renderTargetFilenames";
import { getOrderRequestDashboardHref } from "../../OrdersRoutes";
import { resolveOrderRequestSourceBadgeColor } from "../common/utils/resolveOrderRequestSourceBadgeColor";
import { OrderRequestListingActions } from "./actions/OrderRequestListingActions";
import { PinOrderRequestListingButton } from "./actions/PinOrderRequestListingButton";
import { renderOrderRequestProgress } from "./common/components/renderOrderRequestProgress";
import { renderOrderRequestSoundtracks } from "./common/components/renderOrderRequestSoundtracks";
import { renderOrderRequestStatus } from "./common/components/renderOrderRequestStatus";
import { renderOrderRequestSubtitles } from "./common/components/renderOrderRequestSubtitles";
import { renderOrderRequestVideos } from "./common/components/renderOrderRequestVideos";
import {
    getSortedOrderColumns,
    OrderRequestColumn,
    OrderRequestListingConfig
} from "./config/OrderRequestListingConfig";
import { OrderRequestListingContext } from "./context/OrderRequestListingContext";
import { OrderRequestListingItem, OrderRequestSource } from "./OrderRequestListingItem";

type OrderRequestItem = OrderRequestListingItem & { info: string; progress: string; resolution: string };

type OrderRequestListingColumn = TableColumn<OrderRequestItem>;

export const createOrderRequestListingColumns = (): ListTableColumn<OrderRequestListingItem>[] => {
    const { listingConfig } = useContext(OrderRequestListingContext);

    const columns = getListingColumns(listingConfig);

    return createListTableColumns<OrderRequestItem>(columns);
};

const getListingColumns = (config: OrderRequestListingConfig): OrderRequestListingColumn[] => {
    const columnMapping: Record<OrderRequestColumn, OrderRequestListingColumn> = {
        [OrderRequestColumn.PoNumber]: {
            type: "link",
            key: "pONumber",
            name: "PO Number",
            minWidth: 100,
            maxWidth: 120,
            isSortable: true,
            getHref: (item) => getOrderRequestDashboardHref(item.id)
        },
        [OrderRequestColumn.Airline]: {
            type: "basic",
            key: "airline",
            name: "Airline",
            minWidth: 100,
            maxWidth: 180,
            isSortable: true,
            isResizable: true,
            onRender: renderOptionalName
        },
        [OrderRequestColumn.System]: {
            type: "basic",
            key: "system",
            name: "System",
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            isSortable: true,
            onRender: renderOptionalName
        },
        [OrderRequestColumn.StartDate]: {
            type: "basic",
            key: "startDate",
            name: "Start Date",
            minWidth: 90,
            maxWidth: 90,
            isResizable: true,
            isSortable: true,
            onRender: renderDate
        },
        [OrderRequestColumn.DueDate]: {
            type: "basic",
            key: "dueDate",
            name: "Due Date",
            minWidth: 90,
            maxWidth: 90,
            isResizable: true,
            isSortable: true,
            onRender: renderDate
        },
        [OrderRequestColumn.Progress]: {
            type: "basic",
            key: "progress",
            name: "Progress",
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            isSortable: true,
            onRender: renderOrderRequestProgress
        },
        [OrderRequestColumn.Info]: {
            type: "basic",
            key: "isTrailer",
            name: "Info",
            minWidth: 80,
            maxWidth: 130,
            onRender: renderInfo
        },
        [OrderRequestColumn.Source]: {
            type: "basic",
            key: "source",
            name: "Source",
            minWidth: 60,
            maxWidth: 80,
            isResizable: true,
            onRender: renderSource
        },
        [OrderRequestColumn.TargetFilenames]: {
            type: "basic",
            key: "targetFilenames",
            name: "Target filenames",
            minWidth: 120,
            maxWidth: 200,
            isResizable: true,
            onRender: renderTargetFilenames
        },
        [OrderRequestColumn.Resolution]: {
            type: "basic",
            key: "resolution",
            name: "Resolution",
            minWidth: 110,
            maxWidth: 150,
            isResizable: true,
            onRender: (item) =>
                renderOrderResolutions({
                    targetResolution: item?.targetResolution,
                    sourceResolution: item?.videos.cloud.resolution
                })
        },
        [OrderRequestColumn.Runtime]: {
            type: "basic",
            key: "runtime",
            name: "Runtime",
            minWidth: 60,
            maxWidth: 80,
            isResizable: true,
            onRender: renderRuntime
        },
        [OrderRequestColumn.Video]: {
            type: "basic",
            key: "videos",
            name: "Video",
            isSortable: true,
            minWidth: 40,
            maxWidth: 40,
            isResizable: true,
            onRender: renderOrderRequestVideos
        },
        [OrderRequestColumn.Soundtracks]: {
            type: "basic",
            key: "soundtracks",
            name: "Soundtracks",
            isSortable: true,
            minWidth: 120,
            maxWidth: 160,
            isResizable: true,
            onRender: renderOrderRequestSoundtracks
        },
        [OrderRequestColumn.Subtitles]: {
            type: "basic",
            key: "subtitles",
            name: "Subtitles",
            isSortable: true,
            minWidth: 120,
            maxWidth: 160,
            isResizable: true,
            onRender: renderOrderRequestSubtitles
        },
        [OrderRequestColumn.Status]: {
            type: "basic",
            key: "status",
            name: "Status",
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            onRender: renderOrderRequestStatus
        },
        [OrderRequestColumn.CreatedDate]: {
            type: "basic",
            key: "createdDate",
            name: "Created",
            minWidth: 150,
            maxWidth: 150,
            isSortable: true,
            isResizable: true,
            onRender: renderDateTime
        },
        [OrderRequestColumn.Content]: {
            type: "basic",
            key: "content",
            name: "Content",
            minWidth: 100,
            maxWidth: 400,
            isResizable: true,
            isSortable: true,
            onRender: renderContent
        },
        [OrderRequestColumn.Actions]: {
            type: "buttons",
            key: "actions",
            name: "Actions",
            minWidth: 50,
            maxWidth: 160,
            isResizable: false,
            buttons: [
                {
                    render: (props) => <OrderRequestListingActions {...props} />,
                    id: "actions"
                },
                {
                    id: "pin",
                    render: PinOrderRequestListingButton
                }
            ]
        }
    };

    const columns: OrderRequestListingColumn[] = [];

    getSortedOrderColumns(config.columns).forEach(([key, column]) => {
        const resolvedKey = Number(key) as OrderRequestColumn;

        if (column.visible) {
            columns.push(columnMapping[resolvedKey]);
        }
    });

    return columns;
};

const renderInfo = (item?: OrderRequestListingItem) => {
    ensure(item);

    return (
        <Stack style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "6px" }}>
            {!!item.isTrailer && <Badge color={BadgeColor.ApplicationPrimary} text="Trailer" />}
            {!!item.isRevised && <Badge color={BadgeColor.LightBlue} text="Revised" />}
        </Stack>
    );
};

const renderSource = (item?: OrderRequestListingItem) => {
    ensure(item);

    const badgeColor = resolveOrderRequestSourceBadgeColor(item.source);

    return <Badge color={badgeColor} text={OrderRequestSource[item.source]} />;
};
